<template>
    <el-dialog v-model="visible" custom-class="pure-dialog device-shadow-dialog" :title="title"
        :width="isFullscreen ? '100%' : '90%'" :fullscreen="isFullscreen" @close="handleClose">
        <template #title="{ titleId, titleClass }">
            <div style="display: flex;justify-content: space-between;align-items: center;">
                <span :id="titleId" :class="titleClass">{{ title }}</span>
                <i class="el-icon-full-screen" style="cursor: pointer;" @click="toggleFullscreen"></i>
            </div>
        </template>

        <div v-loading="loading" class="device-shadow-content">
            <div v-if="!loading && !shadowData" class="empty-state">
                <el-empty description="暂无设备影子数据" />
            </div>

            <div v-else-if="shadowData" class="shadow-container">
                <!-- 状态信息并排布局 -->
                <div class="states-row">
                    <!-- 设备状态 -->
                    <el-card class="state-card reported-card" shadow="hover">
                        <template #header>
                            <div class="card-header">
                                <span>设备状态 (Reported)</span>
                            </div>
                        </template>
                        <div class="state-content">
                            <el-table :data="reportedStateData" stripe border
                                :height="isFullscreen ? 'calc(100vh - 180px)' : '600px'" align="center" size="small">
                                <el-table-column prop="key" label="属性名称" align="center" />
                                <el-table-column prop="value" label="当前值" min-width="120" align="center">
                                    <template #default="{ row }">
                                        <span v-if="typeof row.value === 'object'">
                                            <el-popover placement="top" width="400" trigger="click">
                                                <template #reference>
                                                    <el-button size="mini" type="text">查看详情</el-button>
                                                </template>
                                                <pre>{{ JSON.stringify(row.value, null, 2) }}</pre>
                                            </el-popover>
                                        </span>
                                        <span v-else-if="
                                            row.dataType === 'bool' || row.dataType === 'enum'
                                        ">
                                            <el-tag :type="getBooleanTagType(row.originalValue)" size="small">
                                                {{ row.value }}
                                            </el-tag>
                                        </span>
                                        <span v-else>{{ row.value }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="description" label="描述" min-width="140" align="center"
                                    show-overflow-tooltip />
                                <el-table-column prop="timestamp" label="更新时间" width="150" align="center">
                                    <template #default="{ row }">
                                        {{ formatTimestamp(row.timestamp) }}
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-card>

                    <!-- 期望状态 -->
                    <el-card v-if="desiredStateData.length > 0" class="state-card desired-card" shadow="hover">
                        <template #header>
                            <div class="card-header">
                                <span>期望状态 (Desired)</span>
                            </div>
                        </template>
                        <div class="state-content">
                            <el-table :data="desiredStateData" stripe border
                                :height="isFullscreen ? 'calc(100vh - 180px)' : '600px'" align="center" size="small">
                                <el-table-column prop="key" label="属性名称" align="center" />
                                <el-table-column prop="value" label="期望值" min-width="120" align="center">
                                    <template #default="{ row }">
                                        <span v-if="row.dataType === 'bool' || row.dataType === 'enum'">
                                            <el-tag :type="getBooleanTagType(row.originalValue)" size="small">
                                                {{ row.value }}
                                            </el-tag>
                                        </span>
                                        <span v-else>{{ row.value }}</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="description" label="描述" min-width="140" align="center"
                                    show-overflow-tooltip />
                            </el-table>
                        </div>
                    </el-card>

                    <!-- 当没有期望状态时，设备状态占满整行 -->
                    <div v-else class="no-desired-placeholder" />
                </div>
            </div>
        </div>

        <template #footer>
            <div class="dialog-footer">
                <el-button size="mini" @click="handleClose">关闭</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<script>
import { ref, computed, watch } from "vue"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)

export default {
    name: "DeviceShadowDialog",
    components: {
        // IconifyIconOffline
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        shadowData: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        deviceInfo: {
            type: Object,
            default: () => ({ uuid: "", name: "" })
        }
    },
    emits: ["update:modelValue", "close"],
    setup(props, { emit }) {
        const isFullscreen = ref(false)

        const visible = computed({
            get: () => props.modelValue,
            set: (value) => emit("update:modelValue", value)
        })

        // 监听弹窗打开状态，每次打开时重置为非全屏
        watch(
            () => props.modelValue,
            (newValue) => {
                if (newValue) {
                    isFullscreen.value = false
                }
            }
        )

        const title = computed(() => {
            const deviceName = props.deviceInfo?.uuid || props.deviceInfo?.name || "设备"
            return `设备影子信息 - ${deviceName}`
        })

        const reportedStateData = computed(() => {
            if (!props.shadowData?.shadow?.state?.reported) return []

            const reported = props.shadowData.shadow.state.reported
            const metadata = props.shadowData.shadow.metadata || {}
            const tslAttribute = props.shadowData.tsl_attribute || {}

            return Object.entries(reported).map(([key, value]) => {
                const attribute = tslAttribute[key]
                const mappedValue = mapValueBySpecs(value, attribute)
                return {
                    key,
                    value: mappedValue,
                    originalValue: value, // 保留原始值用于调试
                    description: attribute?.desc || "-",
                    timestamp: metadata[key]?.Timestamp || 0,
                    dataType: attribute?.data_type || "",
                    specs: attribute?.specs || ""
                }
            })
        })

        const desiredStateData = computed(() => {
            if (!props.shadowData?.shadow?.state?.desired) return []

            const desired = props.shadowData.shadow.state.desired
            const tslAttribute = props.shadowData.tsl_attribute || {}

            return Object.entries(desired).map(([key, value]) => {
                const attribute = tslAttribute[key]
                const mappedValue = mapValueBySpecs(value, attribute)

                return {
                    key,
                    value: mappedValue,
                    originalValue: value, // 保留原始值用于调试
                    description: attribute?.desc || "-",
                    dataType: attribute?.data_type || "",
                    specs: attribute?.specs || ""
                }
            })
        })

        const toggleFullscreen = () => {
            isFullscreen.value = !isFullscreen.value
        }

        const handleClose = () => {
            emit("close")
        }

        const formatTimestamp = (timestamp) => {
            if (!timestamp || timestamp === 0) return "无"
            return dayjs.unix(timestamp).utc().format("YYYY-MM-DD HH:mm:ss")
        }

        // 根据TSL属性映射值
        const mapValueBySpecs = (value, attribute) => {
            if (!attribute || !attribute.specs) return value

            const dataType = attribute.data_type

            // 只处理bool和enum类型
            if (dataType !== "bool" && dataType !== "enum") return value

            try {
                const specs = attribute.specs
                const mappedValue = specs[String(value)]
                return mappedValue !== undefined ? mappedValue : value
            } catch (error) {
                console.warn("Failed to parse specs:", attribute.specs, error)
                return value
            }
        }

        // 获取布尔值标签类型
        const getBooleanTagType = (originalValue) => {
            // 对于布尔类型，true显示为success，false显示为danger
            if (typeof originalValue === "boolean") {
                return originalValue ? "success" : "danger"
            }
            // 对于数值类型，1显示为success，0显示为danger
            if (typeof originalValue === "number") {
                return originalValue === 1 ? "success" : "danger"
            }
            // 对于字符串类型，"1"显示为success，"0"显示为danger
            if (typeof originalValue === "string") {
                return originalValue === "1" ? "success" : "danger"
            }
            // 默认为info
            return "info"
        }

        return {
            isFullscreen,
            visible,
            title,
            reportedStateData,
            desiredStateData,
            toggleFullscreen,
            handleClose,
            formatTimestamp,
            mapValueBySpecs,
            getBooleanTagType,
        }
    }
}
</script>

<style>
.pure-dialog .el-dialog__header {
    padding-right: 45px;
}

.pure-dialog .el-dialog__headerbtn {
    top: 20px;
    right: 12px;
    width: 24px;
    height: 24px;
}

.pure-dialog .pure-dialog-svg {
    color: var(--el-color-info);
}

.pure-dialog .el-dialog__footer {
    padding-top: 0;
}

.pure-dialog .pure-dialog-svg {
    width: 18px;
    height: 18px;
}

/* 设备影子对话框样式优化 */
.device-shadow-dialog .el-dialog__body {
    padding: 6px 16px;
}
</style>

<style scoped>
.device-shadow-content {
    min-height: 200px;
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.shadow-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
}

.states-row {
    display: flex;
    gap: 16px;
    height: 100%;
    min-height: 650px;
}



.state-card {
    flex: 1;
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
}

.reported-card {
    min-width: 0;
    /* 允许flex收缩 */
}

.desired-card {
    min-width: 0;
    /* 允许flex收缩 */
}

.no-desired-placeholder {
    flex: 1;
    display: none;
    /* 隐藏占位符，让设备状态卡片自动扩展 */
}

/* 当没有期望状态时，让设备状态卡片占满整行 */
.states-row:has(.no-desired-placeholder) .reported-card {
    flex: 1;
    max-width: 100%;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.state-content {
    flex: 1;
    overflow: hidden;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 10px;
}

pre {
    background-color: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.4;
    max-height: 300px;
    overflow-y: auto;
}


/* 响应式布局 */
@media (max-width: 1200px) {
    .states-row {
        flex-direction: column;
        gap: 12px;
    }

    .state-card {
        flex: none;
    }

    .state-content .el-table {
        height: 450px !important;
    }
}

/* 表格样式优化 */
.state-content .el-table {
    border-radius: 4px;
}

.state-content .el-table th {
    background-color: #fafafa;
    font-weight: 600;
}

.state-content .el-table--small .el-table__cell {
    padding: 8px 0;
}
</style>